import { jwtDecode, JwtPayload } from 'jwt-decode';

const isBrowser = () => typeof window !== 'undefined';

const getStorageItem = (storage: Storage | undefined, key: string) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const setStorageItem = (storage: Storage | undefined, key: string, value: string) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // Safari can reject storage access in private or restricted contexts.
  }
};

const removeStorageItem = (storage: Storage | undefined, key: string) => {
  try {
    storage?.removeItem(key);
  } catch {
    // Ignore blocked storage access.
  }
};

const isLoginPath = () => isBrowser() && window.location.pathname === '/login';

export const getStoredToken = () => {
  if (!isBrowser()) {
    return null;
  }

  return getStorageItem(window.localStorage, 'token') ?? getStorageItem(window.sessionStorage, 'token');
};

export const getStoredAzureFlag = () => {
  if (!isBrowser()) {
    return null;
  }

  return getStorageItem(window.localStorage, 'azure') ?? getStorageItem(window.sessionStorage, 'azure');
};

export const clearAuthStorage = () => {
  if (!isBrowser()) {
    return;
  }

  removeStorageItem(window.localStorage, 'token');
  removeStorageItem(window.localStorage, 'azure');
  removeStorageItem(window.sessionStorage, 'token');
  removeStorageItem(window.sessionStorage, 'azure');
};

export const persistAuthToken = (token: string, azure: boolean) => {
  if (!isBrowser()) {
    return;
  }

  setStorageItem(window.localStorage, 'token', token);
  setStorageItem(window.localStorage, 'azure', String(azure));
  setStorageItem(window.sessionStorage, 'token', token);
  setStorageItem(window.sessionStorage, 'azure', String(azure));
};

export const getPostLoginRedirect = () => {
  if (!isBrowser()) {
    return '/';
  }

  const redirect = getStorageItem(window.sessionStorage, 'redirectAfterLogin');
  removeStorageItem(window.sessionStorage, 'redirectAfterLogin');

  if (!redirect || redirect === '/login' || redirect.startsWith('/login?')) {
    return '/';
  }

  return redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/';
};

export const getDecodedToken = (): JwtPayload | undefined => {
  const token = getStoredToken();

  if (typeof token !== 'string' || token.trim().length === 0) {
    return undefined;
  }

  try {
    const decoded = jwtDecode<JwtPayload>(token);

    if (typeof decoded.exp === 'number' && decoded.exp * 1000 <= Date.now()) {
      clearAuthStorage();
      return undefined;
    }

    return decoded;
  } catch {
    clearAuthStorage();
    return undefined;
  }
};

export const getTokenSubject = () => {
  const decoded = getDecodedToken();
  return typeof decoded?.sub === 'string' ? decoded.sub : '';
};

export const redirectToLogin = () => {
  if (!isBrowser() || isLoginPath()) {
    return;
  }

  const currentPath = window.location.pathname + window.location.search;

  clearAuthStorage();
  setStorageItem(window.sessionStorage, 'redirectAfterLogin', currentPath);

  window.location.replace('/login');
};

export const requireTokenSubject = () => {
  const subject = getTokenSubject();

  if (!subject) {
    redirectToLogin();
  }

  return subject;
};
